import { Stack } from '@mui/material';
import { translateKey } from 'src/locales/translate';
import { WarehouseProductDto } from 'woofwoof-api';
import WarehouseBadge from '../WarehouseBadge';

type BadgeSource = Pick<
  WarehouseProductDto,
  | 'isRx'
  | 'controlledCategory'
  | 'isNegative'
  | 'isBelowMinimum'
  | 'hasExpiringBatch'
  | 'unbatchedIssueCount'
  | 'archivedAt'
>;

export interface ProductBadge {
  key: string;
  label: string;
  hint: string;
  color: 'info' | 'warning' | 'error' | 'default';
  icon: string;
}

export function getProductBadges(product: BadgeSource): ProductBadge[] {
  const badge = (
    key: string,
    color: ProductBadge['color'],
    icon: string,
    options?: Record<string, unknown>,
  ): ProductBadge => ({
    key,
    color,
    icon,
    label: translateKey(`warehouse.products.badges.${key}`, options),
    hint: translateKey(`warehouse.products.badges.${key}Hint`, options),
  });
  return [
    ...(product.isRx
      ? [badge('rx', 'info', 'solar:document-medicine-bold')]
      : []),
    ...(product.controlledCategory
      ? [
          badge('controlled', 'warning', 'solar:shield-warning-bold', {
            category: product.controlledCategory,
          }),
        ]
      : []),
    ...(product.isNegative
      ? [badge('negative', 'error', 'solar:minus-circle-bold')]
      : []),
    ...(product.isBelowMinimum
      ? [badge('belowMinimum', 'warning', 'solar:graph-down-bold')]
      : []),
    ...(product.hasExpiringBatch
      ? [badge('expiring', 'warning', 'solar:calendar-mark-bold')]
      : []),
    ...(product.unbatchedIssueCount > 0
      ? [
          badge('unassigned', 'warning', 'solar:question-circle-bold', {
            count: product.unbatchedIssueCount,
          }),
        ]
      : []),
    ...(product.archivedAt
      ? [badge('archived', 'default', 'solar:archive-bold')]
      : []),
  ];
}

interface WarehouseProductBadgesProps {
  product: BadgeSource;
}

export default function WarehouseProductBadges({
  product,
}: WarehouseProductBadgesProps): JSX.Element {
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {getProductBadges(product).map((item) => (
        <WarehouseBadge
          key={item.key}
          label={item.label}
          hint={item.hint}
          color={item.color}
          icon={item.icon}
        />
      ))}
    </Stack>
  );
}
